import { STRING_UOM } from '@/main/app.constants';
import type { BackendDuration } from '@/services/systemConfiguration.types';
import type { ThresholdMetricStoreState } from '@/tools/thresholdMetric/thresholdMetric.store';
import type { ThresholdOutputV1 } from '@seeqdev/api/model/ThresholdOutputV1';

export interface DurationPayload {
  value: number;
  units: string;
}

/**
 * Gets the string representation of the threshold, which will be an ID if the threshold is an item, or else a
 * numeric value with optional units if the threshold is a value.
 */
export function getThresholdString(threshold: ThresholdOutputV1): string {
  if (threshold.isGenerated) {
    const generatedValue = threshold.value;
    if (!generatedValue) {
      return '';
    }
    const unit = generatedValue.uom === STRING_UOM || !generatedValue.uom ? '' : generatedValue.uom;
    return `${generatedValue.value} ${unit}`.trim();
  }

  return threshold?.item?.id ?? '';
}

/**
 * Converts a backend `{ value, uom }` duration into the frontend `{ value, units }` shape. Reimplemented locally
 * (rather than importing `@/datetime/dateTime.utilities`) to avoid a module cycle back through `core.stores.ts`,
 * which the store's legacy singleton bridge is created from.
 */
export function toFrontendDuration(duration: BackendDuration | undefined): DurationPayload | undefined {
  if (duration === undefined) {
    return undefined;
  }

  return { value: duration.value, units: duration.uom };
}

export function applyThreshold(draft: ThresholdMetricStoreState, level: number, threshold: string | undefined): void {
  if (threshold === undefined) {
    delete draft.thresholds[level];
  } else {
    draft.thresholds[level] = { ...draft.thresholds[level], threshold };
  }

  if (Object.keys(draft.thresholds).length === 0) {
    draft.neutralColor = undefined;
  }
}

export function applyThresholdColor(draft: ThresholdMetricStoreState, level: number, color: string): void {
  if (level === 0) {
    draft.neutralColor = color;
  } else {
    draft.thresholds[level] = { ...draft.thresholds[level], color };
  }
}
